import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../config/api";
import webSocketManager from "../utils/websocketUtils";
import Logo from "../assets/Images/Logo-white.png";

const LeaderboardTable = ({ teams }) => {
  if (!teams || teams.length === 0) {
    return (
      <p className="text-sm text-gray-500 text-center py-8 bg-white rounded-lg border border-gray-200">
        No teams yet
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <th className="px-3 py-3 w-10">#</th>
            <th className="px-3 py-3">Team</th>
            <th className="px-3 py-3 text-center w-14">TP</th>
            <th className="px-3 py-3 text-center w-14">MP</th>
            <th className="px-3 py-3 text-center w-12">W</th>
          </tr>
        </thead>
        <tbody>
          {teams.map((team, index) => (
            <tr
              key={team.teamId}
              className="border-b border-gray-100 last:border-0"
            >
              <td className="px-3 py-3 font-medium text-gray-500">
                {index + 1}
              </td>
              <td className="px-3 py-3">
                <div className="flex items-center gap-2 min-w-0">
                  {team.logo ? (
                    <img
                      src={team.logo}
                      alt=""
                      className="h-8 w-8 rounded-full object-cover flex-shrink-0 border border-gray-200"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-[#001e48] flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                      {team.teamName.charAt(0)}
                    </div>
                  )}
                  <span className="font-medium text-gray-900 truncate">
                    {team.teamName}
                  </span>
                </div>
              </td>
              <td className="px-3 py-3 text-center font-medium text-gray-800">
                {team.teamPoint ?? 0}
              </td>
              <td className="px-3 py-3 text-center font-medium text-gray-800">
                {team.matchPoint ?? 0}
              </td>
              <td className="px-3 py-3 text-center font-medium text-gray-800">
                {team.wins ?? 0}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const sortTeamsByScore = (teams) =>
  [...teams].sort((a, b) => {
    const teamPointDiff = (b.teamPoint || 0) - (a.teamPoint || 0);
    if (teamPointDiff !== 0) return teamPointDiff;
    const matchPointDiff = (b.matchPoint || 0) - (a.matchPoint || 0);
    if (matchPointDiff !== 0) return matchPointDiff;
    return (b.wins || 0) - (a.wins || 0);
  });

const headerGradient = {
  background:
    "linear-gradient(99.32deg, rgb(1, 30, 72) -0.41%, rgb(17, 64, 132) 93.61%)",
};

const BackButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute top-2 right-2 bg-white text-gray-800 p-2.5 rounded-full shadow-md"
    aria-label="Back to categories"
  >
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  </button>
);

const PageHeader = ({ title, onBack }) => (
  <div style={headerGradient} className="text-white p-6 pb-8 relative">
    <BackButton onClick={onBack} />
    <h1 className="text-2xl font-bold text-center mb-3 mt-8">{title}</h1>
    <p className="text-center text-sm opacity-90 mb-3">Powered by</p>
    <div className="flex justify-center">
      <img src={Logo} alt="PLLAYER" className="h-8" />
    </div>
  </div>
);

const LEADERBOARD_VIEW_KEY = "scorecard-leaderboard-show-all-groups";

const Leaderboard = () => {
  const navigate = useNavigate();
  const [leaderboardGroups, setLeaderboardGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const [showAllGroups, setShowAllGroups] = useState(() => {
    try {
      return localStorage.getItem(LEADERBOARD_VIEW_KEY) === "true";
    } catch {
      return false;
    }
  });

  const handleViewModeChange = (allGroups) => {
    setShowAllGroups(allGroups);
    try {
      localStorage.setItem(LEADERBOARD_VIEW_KEY, String(allGroups));
    } catch {
      // ignore storage errors
    }
  };

  const fetchTeams = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.TEAMS_LEADERBOARD);
      const data = await response.json();
      if (data.success) {
        if (data.data?.groups) {
          setLeaderboardGroups(data.data.groups);
        } else if (Array.isArray(data.data)) {
          setLeaderboardGroups([
            { groupId: 0, groupName: "All Teams", teams: data.data },
          ]);
        }
      }
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  useEffect(() => {
    const ws = webSocketManager.connect(
      "https://api-dpl-684526581556.asia-south1.run.app"
    );

    const handleConnect = () => {
      ws.emit("joinLiveScore");
    };

    webSocketManager.on("connect", handleConnect);
    webSocketManager.on("reconnect", handleConnect);
    setSocket(ws);

    return () => {
      webSocketManager.off("connect", handleConnect);
      webSocketManager.off("reconnect", handleConnect);
    };
  }, []);

  useEffect(() => {
    if (!socket) return;

    const handleLeaderboardUpdate = (data) => {
      if (data.groups) {
        setLeaderboardGroups(data.groups);
      } else if (data.teams) {
        setLeaderboardGroups([
          { groupId: 0, groupName: "All Teams", teams: data.teams },
        ]);
      }
    };

    const refreshLeaderboard = () => {
      fetchTeams();
    };

    socket.on("leaderboardUpdate", handleLeaderboardUpdate);
    socket.on("scoreUpdate", refreshLeaderboard);
    socket.on("setEnd", refreshLeaderboard);
    socket.on("matchEnd", refreshLeaderboard);
    socket.on("undoScore", refreshLeaderboard);

    return () => {
      socket.off("leaderboardUpdate", handleLeaderboardUpdate);
      socket.off("scoreUpdate", refreshLeaderboard);
      socket.off("setEnd", refreshLeaderboard);
      socket.off("matchEnd", refreshLeaderboard);
      socket.off("undoScore", refreshLeaderboard);
    };
  }, [socket, fetchTeams]);

  const combinedTeams = sortTeamsByScore(
    leaderboardGroups.flatMap((group) => group.teams || [])
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Leaderboard" onBack={() => navigate("/")} />
        <p className="text-center text-gray-500 py-12">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader title="Leaderboard" onBack={() => navigate("/")} />

      <div className="max-w-md mx-auto p-4 space-y-4">
        <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-800">
                Show all groups
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {showAllGroups
                  ? "One combined ranking"
                  : "Separate tables per group"}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={showAllGroups}
              onClick={() => handleViewModeChange(!showAllGroups)}
              className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${
                showAllGroups ? "bg-[#001e48]" : "bg-gray-300"
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${
                  showAllGroups ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>
        </div>

        <p className="text-xs text-gray-500 text-center">
          TP = Team Points · MP = Match Points · W = Wins
        </p>

        {showAllGroups ? (
          <LeaderboardTable teams={combinedTeams} />
        ) : (
          leaderboardGroups.map((group) => (
            <div key={group.groupId}>
              <h2 className="text-sm font-semibold text-[#001e48] mb-2 px-1">
                {group.groupName}
              </h2>
              <LeaderboardTable teams={group.teams} />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
